import { API_BASE } from './api';
import { parseModbusData } from './modbusDecoder';
import { buildParamKey } from './widgetKeys';
import { extractRecordModbus, withAnalysisFlag } from './telemetrySource';
import { getParamAddress } from './bmsConstants';

const WIDGET_STORAGE_KEY = 'bms_widgets_state';
const CHUNK_HOURS = 24;

/**
 * @typedef {Object} WidgetBatterySelection
 * @property {string} deviceId
 * @property {number} slaveId
 * @property {string} name
 * @property {string} [type]
 *
 * @typedef {Object} WidgetParameter
 * @property {string} key
 * @property {number} address
 * @property {string} name
 *
 * @typedef {Object} WidgetConfig
 * @property {string[]} selectedDevices
 * @property {{startDate: string, endDate: string}} dateRange
 * @property {WidgetBatterySelection[]} selectedBatteries
 * @property {WidgetParameter[]} selectedParameters
 *
 * @typedef {Object} Widget
 * @property {string} id
 * @property {string} title
 * @property {string} createdAt
 * @property {string} lastUpdated
 * @property {WidgetConfig} config
 * @property {boolean} [isLoading]
 * @property {string|null} [error]
 * @property {string[]} [partialErrors]
 * @property {Array<Object>} [data]
 *
 * @typedef {Object} WidgetLayoutItem
 * @property {string} id
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
 */

class WidgetManager {
  constructor() {
    this.state = this.loadState();
    this.listeners = [];
  }

  loadState() {
    try {
      const stored = localStorage.getItem(WIDGET_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          widgets: parsed.widgets || [],
          layout: parsed.layout || []
        };
      }
    } catch (e) {
      console.error('Failed to load widget state:', e);
    }
    return { widgets: [], layout: [] };
  }

  saveState() {
    try {
      // Exclude telemetry datasets from storage (prevent quota exceed)
      const storageState = {
        widgets: this.state.widgets.map(({ id, title, config, createdAt, lastUpdated }) => ({
          id, title, config, createdAt, lastUpdated
        })),
        layout: this.state.layout
      };
      localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(storageState));
    } catch (e) {
      console.error('Failed to save widget state:', e);
    }
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    const stateCopy = { widgets: [...this.state.widgets], layout: [...this.state.layout] };
    this.listeners.forEach(l => l(stateCopy));
  }

  getState() {
    return { widgets: [...this.state.widgets], layout: [...this.state.layout] };
  }

  createWidget(title, config) {
    const id = `widget_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const newWidget = {
      id,
      title,
      config,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      isLoading: false,
      error: null,
      partialErrors: []
    };

    const newLayout = {
      id,
      x: 0,
      y: this.getMaxY() + 1,
      width: 6,
      height: 4
    };

    this.state.widgets.push(newWidget);
    this.state.layout.push(newLayout);
    this.saveState();
    this.notify();
    return id;
  }

  updateWidget(id, updates) {
    const index = this.state.widgets.findIndex(w => w.id === id);
    if (index === -1) return;

    this.state.widgets[index] = {
      ...this.state.widgets[index],
      ...updates,
      lastUpdated: new Date().toISOString()
    };

    if (!updates.data) {
      this.saveState();
    }
    this.notify();
  }

  deleteWidget(id) {
    this.state.widgets = this.state.widgets.filter(w => w.id !== id);
    this.state.layout = this.state.layout.filter(l => l.id !== id);
    this.saveState();
    this.notify();
  }

  duplicateWidget(id) {
    const source = this.state.widgets.find(w => w.id === id);
    if (!source) return null;
    return this.createWidget(`${source.title} (Copy)`, source.config);
  }

  exportConfig(id) {
    const w = this.state.widgets.find(w => w.id === id);
    return w ? JSON.stringify({ title: w.title, config: w.config }, null, 2) : null;
  }

  importConfig(jsonStr) {
    try {
      const { title, config } = JSON.parse(jsonStr);
      return this.createWidget(title, config);
    } catch (e) {
      return null;
    }
  }

  getMaxY() {
    if (this.state.layout.length === 0) return -1;
    return Math.max(...this.state.layout.map(item => item.y + item.height - 1));
  }

  buildChunks(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffHours = (end - start) / (1000 * 60 * 60);

    if (diffHours <= CHUNK_HOURS) {
      return [{ start: start.toISOString(), end: end.toISOString() }];
    }

    const chunks = [];
    let curr = new Date(start);
    while (curr < end) {
      const next = new Date(curr.getTime() + CHUNK_HOURS * 60 * 60 * 1000);
      const chunkEnd = next > end ? end : next;
      chunks.push({ start: curr.toISOString(), end: chunkEnd.toISOString() });
      curr = chunkEnd;
    }
    return chunks;
  }

  async fetchDeviceRecords(deviceId, dateRange, token) {
    const chunks = this.buildChunks(dateRange.startDate, dateRange.endDate);
    let deviceRecords = [];

    for (const chunk of chunks) {
      const rawUrl = `${API_BASE}/api/getLocationInterval?api_access_token=${token}&deviceId=${deviceId}&startDate=${chunk.start}&endDate=${chunk.end}`;
      const url = withAnalysisFlag(rawUrl);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Telemetry fetch failed for device ${deviceId} (HTTP ${res.status})`);
      }
      const json = await res.json();
      const records = json.locationInfo || (Array.isArray(json) ? json : []);
      deviceRecords = deviceRecords.concat(records);
    }

    return deviceRecords;
  }

  processDeviceRecords(deviceId, deviceRecords, selectedBatteries, selectedParameters) {
    const batteriesForDevice = selectedBatteries.filter(bat => bat.deviceId === deviceId);

    return deviceRecords.map(item => {
      const row = {
        timestamp: new Date(item.timestamp || item.localTimeStamp).getTime(),
        deviceId,
        speed: item.speed,
        latitude: item.latitude,
        longitude: item.longitude
      };

      batteriesForDevice.forEach(bat => {
        let decoded = [];
        const rs485modbus = extractRecordModbus(item);
        if (rs485modbus) {
          decoded = parseModbusData(rs485modbus, bat.slaveId, bat.type);
        }

        selectedParameters.forEach(param => {
          const address = getParamAddress(param.key, bat.type) ?? param.address;
          const regMatch = decoded.find(reg => reg.address === address);
          const valKey = buildParamKey(deviceId, bat.slaveId, param.key);
          row[valKey] = regMatch
            ? { value: regMatch.parsedValue, unit: regMatch.unit, description: regMatch.description }
            : { value: 'N/A', unit: '' };
        });
      });

      return row;
    });
  }


  // Fetches each selected device independently. One device failing does not
  // discard telemetry already fetched for the others in the same widget.
  async loadWidgetData(id, token) {
    const widget = this.state.widgets.find(w => w.id === id);
    if (!widget) return;

    this.updateWidget(id, { isLoading: true, error: null, partialErrors: [] });

    const { selectedDevices, dateRange, selectedBatteries, selectedParameters } = widget.config;
    const allDevicesData = [];
    const partialErrors = [];

    for (const deviceId of selectedDevices) {
      try {
        const deviceRecords = await this.fetchDeviceRecords(deviceId, dateRange, token);
        const processedRecords = this.processDeviceRecords(deviceId, deviceRecords, selectedBatteries, selectedParameters);
        allDevicesData.push(...processedRecords);
      } catch (err) {
        console.error(err);
        partialErrors.push(`${deviceId}: ${err.message || 'Failed to load telemetry'}`);
      }
    }

    if (allDevicesData.length === 0 && partialErrors.length > 0) {
      this.updateWidget(id, { error: partialErrors.join('; '), isLoading: false, partialErrors: [] });
      return;
    }

    this.updateWidget(id, { data: allDevicesData, isLoading: false, partialErrors });
  }
}

export const widgetManager = new WidgetManager();
export { WidgetManager };
